/**
 * Shared grouping logic for task list widgets.
 */
import type { Todo } from '../types';

type GroupBy = 'none' | 'status' | 'date' | 'horizon';

interface GroupedTodos {
  key: string;
  label: string;
  todos: Todo[];
}

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  waiting: 'Waiting',
  canceled: 'Canceled',
};

const HORIZON_LABELS: Record<string, string> = {
  now: 'Now',
  next: 'Next',
  soon: 'Soon',
  later: 'Later',
  someday: 'Someday',
  _none: 'Inbox',
};

const STATUS_ORDER: Record<string, number> = {
  in_progress: 0,
  not_started: 1,
  waiting: 2,
  completed: 3,
  canceled: 4,
};

const HORIZON_ORDER: Record<string, number> = {
  now: 0,
  next: 1,
  soon: 2,
  later: 3,
  someday: 4,
  _none: 5,
};

function getDateKey(todo: Todo): string {
  const d = todo.deadline;
  if (!d) return '_none';
  return d.split('T')[0];
}

function formatDateLabel(key: string): string {
  if (key === '_none') return 'No Date';
  const date = new Date(key + 'T00:00:00');
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function groupTodos(
  todos: Todo[],
  groupBy: GroupBy | string | undefined,
): GroupedTodos[] {
  if (!groupBy || groupBy === 'none') {
    return [{ key: '_all', label: '', todos }];
  }

  // Silently handle unknown groupBy values from saved configs
  if (!['status', 'date', 'horizon'].includes(groupBy)) {
    return [{ key: '_all', label: '', todos }];
  }

  const groups = new Map<string, Todo[]>();

  for (const todo of todos) {
    let key: string;
    switch (groupBy) {
      case 'status':
        key = todo.status || '_unknown';
        break;
      case 'date':
        key = getDateKey(todo);
        break;
      case 'horizon':
        key = todo.horizon || '_none';
        break;
      default:
        key = '_all';
    }

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(todo);
  }

  const entries = Array.from(groups.entries()).map(([key, groupTodos]) => ({
    key,
    label: getGroupLabel(groupBy, key),
    todos: groupTodos,
  }));

  // Sort groups in a logical order
  entries.sort((a, b) => {
    switch (groupBy) {
      case 'status':
        return (STATUS_ORDER[a.key] ?? 99) - (STATUS_ORDER[b.key] ?? 99);
      case 'horizon':
        return (HORIZON_ORDER[a.key] ?? 99) - (HORIZON_ORDER[b.key] ?? 99);
      case 'date':
        if (a.key === '_none') return 1;
        if (b.key === '_none') return -1;
        return a.key.localeCompare(b.key);
      default:
        return 0;
    }
  });

  return entries;
}

export function getGroupLabel(
  groupBy: string,
  key: string,
): string {
  switch (groupBy) {
    case 'status':
      return STATUS_LABELS[key] || key;
    case 'horizon':
      return HORIZON_LABELS[key] || key;
    case 'date':
      return formatDateLabel(key);
    default:
      return '';
  }
}
